import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Menu, X, Phone, ChevronRight, Mail, BadgeCheck } from "lucide-react";
import { useSiteAboutData } from "../hooks/useSiteData";
import { prefetchBannerType } from "../api/queries";
import { ROUTE_BANNER_TYPES } from "../lib/banners";
import BrandLockup from "./BrandLockup";
import { getCompanyBranding } from "../lib/site";
import { VISIBLE_NAV_ITEMS } from "../lib/nav";

const navItems = VISIBLE_NAV_ITEMS;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { data: companyInfo = { phone: "", primaryPhone: "", email: "" } } = useSiteAboutData();
  const { legalName } = getCompanyBranding(companyInfo);
  const location = useLocation();
  const queryClient = useQueryClient();

  const prefetchBannerForRoute = (path) => {
    const bannerType = ROUTE_BANNER_TYPES[path];
    if (bannerType) {
      prefetchBannerType(queryClient, bannerType).catch(() => {});
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="sticky top-0 z-50">
      <div className="hidden md:block bg-gradient-to-r from-[#071628] via-[#0b1f3a] to-[#071628] text-white/90 text-xs border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex justify-between items-center gap-6">
          <div className="flex items-center gap-2 min-w-0">
            <BadgeCheck className="w-3.5 h-3.5 text-[#aad4fa] flex-shrink-0" />
            <span className="truncate font-medium tracking-wide">
              ISO 9001:2015 Certified
            </span>
            <span className="text-white/25 hidden lg:inline">|</span>
            <span className="truncate hidden lg:inline text-white/75">
              NSIC &amp; MSME Registered
            </span>
          </div>
          <div className="flex items-center gap-5 flex-shrink-0">
            {companyInfo.primaryPhone && (
              <a
                href={`tel:${companyInfo.primaryPhone}`}
                className="flex items-center gap-2 hover:text-[#aad4fa] transition-colors font-medium"
              >
                <Phone className="w-3.5 h-3.5" />
                {companyInfo.primaryPhone}
              </a>
            )}
            {companyInfo.email && (
              <a
                href={`mailto:${companyInfo.email}`}
                className="hidden sm:flex items-center gap-2 hover:text-[#aad4fa] transition-colors font-medium"
              >
                <Mail className="w-3.5 h-3.5" />
                {companyInfo.email}
              </a>
            )}
          </div>
        </div>
      </div>

      <header
        className={`transition-all duration-300 border-b ${
          scrolled
            ? "bg-white/95 backdrop-blur-lg shadow-md border-slate-200/80"
            : "bg-white border-slate-100 shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-3.5 md:py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="group flex-shrink-0 min-w-0"
            aria-label={`${legalName} — Home`}
          >
            <BrandLockup about={companyInfo} variant="light" />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-end min-w-0">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onMouseEnter={() => prefetchBannerForRoute(item.to)}
                onFocus={() => prefetchBannerForRoute(item.to)}
                className={({ isActive }) =>
                  `relative px-2 xl:px-3 py-2 text-[13px] xl:text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? "text-[#1758a8] bg-[#e8f2fc]/80"
                      : "text-slate-600 hover:text-[#1758a8] hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/contact"
            className="hidden lg:inline-flex shrink-0 items-center gap-2 whitespace-nowrap bg-[#1e6fcf] hover:bg-[#1758a8] text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-md shadow-[#1e6fcf]/20 hover:shadow-lg hover:shadow-[#1e6fcf]/25 transition-all duration-300 group"
          >
            Get a Quote
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2.5 rounded-lg text-[#0b1f3a] hover:bg-slate-100 transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-xl">
            <nav className="flex flex-col py-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onTouchStart={() => prefetchBannerForRoute(item.to)}
                  className={({ isActive }) =>
                    `mx-3 my-0.5 px-4 py-3 text-base font-semibold rounded-lg transition-colors ${
                      isActive
                        ? "text-[#1758a8] bg-[#e8f2fc]"
                        : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mx-3 my-3 inline-flex items-center justify-center gap-2 bg-[#1e6fcf] hover:bg-[#1758a8] text-white font-semibold text-sm px-5 py-3 rounded-lg"
              >
                Get a Quote <ChevronRight className="w-4 h-4" />
              </Link>
            </nav>
          </div>
        )}
      </header>
    </div>
  );
}
